import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { resolveImageUrl } from '../lib/api';
import { useLang } from '../hooks/useLang';
import { pickLocalized } from '../i18n';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Projets = () => {
  const { t } = useTranslation();
  const { lang, routes } = useLang();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  const filters = useMemo(() => ([
    { key: 'all', label: t('projects.filterAll'), match: () => true },
    { key: 'logements', label: t('projects.filterHousing'), match: (c) => /logement|housing/i.test(c) },
    { key: 'equipement', label: t('projects.filterPublic'), match: (c) => /équipement|equipement|public/i.test(c) },
    { key: 'amenagement', label: t('projects.filterUrban'), match: (c) => /aménagement|amenagement|urban/i.test(c) },
  ]), [t]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/projects`);
        if (!response.ok) throw new Error('Failed to fetch projects');
        const data = await response.json();
        setProjects(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching projects:', err);
        setError(err.message);
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  // Filters match on FR category (canonical), since EN translations may vary
  const counts = useMemo(() => {
    const c = {};
    filters.forEach((f) => { c[f.key] = projects.filter((p) => f.match(p.category || '')).length; });
    return c;
  }, [projects, filters]);

  const filteredProjects = useMemo(() => {
    const f = filters.find((x) => x.key === activeFilter) || filters[0];
    return projects.filter((p) => f.match(p.category || ''));
  }, [projects, activeFilter, filters]);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-grow pt-32 pb-20 px-8 lg:px-16">
        <div className="max-w-[1400px] mx-auto">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-wide mb-4">{t('projects.title')}</h1>
          <p className="text-lg font-light text-gray-600 mb-10">{t('projects.subtitle')}</p>

          {!loading && !error && projects.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 mb-16 border-b border-gray-200 pb-6" data-testid="category-filters">
              {filters.map((f) => {
                const isActive = activeFilter === f.key;
                const count = counts[f.key] || 0;
                return (
                  <button key={f.key} type="button" onClick={() => setActiveFilter(f.key)} data-testid={`filter-${f.key}`}
                    className={`relative text-sm font-light tracking-wide uppercase pb-2 transition-colors duration-200 ${
                      isActive ? 'text-black border-b-2 border-[#d94e47] -mb-[1px]' : 'text-gray-500 hover:text-black'
                    }`}>
                    {f.label}
                    <span className="ml-2 text-xs text-gray-400">({count})</span>
                  </button>
                );
              })}
            </div>
          )}

          {loading && <div className="text-center py-20"><p className="text-gray-500 font-light">{t('common.loading')}</p></div>}
          {error && <div className="text-center py-20"><p className="text-red-500 font-light">{t('common.error')}: {error}</p></div>}

          {!loading && !error && filteredProjects.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="projects-grid">
              {filteredProjects.map((project) => {
                const title = pickLocalized(project, 'title', lang);
                const category = pickLocalized(project, 'category', lang);
                const location = pickLocalized(project, 'location', lang);
                return (
                  <Link key={project.slug} to={routes.project(project.slug)} className="group cursor-pointer" data-testid={`project-card-${project.slug}`}>
                    <div className="relative aspect-[4/3] overflow-hidden mb-4">
                      {project.images && project.images.length > 0 ? (
                        <img src={resolveImageUrl(project.images[0].url)} alt={title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      ) : (
                        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                          <span className="text-gray-400">{t('common.noImage')}</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-xs font-light tracking-wider text-gray-500 mb-2 uppercase">{category}</p>
                      <h3 className="text-xl font-light mb-2 group-hover:text-[#d94e47] transition-colors duration-300">{title}</h3>
                      <p className="text-sm font-light text-gray-600">{location}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          {!loading && !error && projects.length === 0 && (
            <div className="text-center py-20"><p className="text-gray-500 font-light">{t('projects.empty')}</p></div>
          )}
          {!loading && !error && projects.length > 0 && filteredProjects.length === 0 && (
            <div className="text-center py-20" data-testid="filter-empty-state"><p className="text-gray-500 font-light">{t('projects.filterEmpty')}</p></div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Projets;
